import type { FeatureKey } from '@/lib/features'
import { hasFeature } from '@/lib/features'

/**
 * Why the upgrade dialog opened. It decides which tier is recommended and
 * what the dialog says first.
 */
export type UpgradeReason =
  | { kind: 'credits' }
  | { kind: 'feature'; feature: FeatureKey }
  | { kind: 'watermark' }
  | { kind: 'browse' }

export interface UpgradeCandidate {
  code: string
  monthly_credits: number
  is_managed: boolean
  features: Partial<Record<FeatureKey, boolean>>
  stripe_price_id_monthly: string | null
  stripe_price_id_yearly: string | null
}

/**
 * The tiers a customer can move UP to: self-serve, sold through Stripe, and
 * bigger than what they have. Ordered by size.
 */
export function upgradeCandidates<T extends UpgradeCandidate>(
  plans: T[],
  currentCode: string | null
): T[] {
  const current = plans.find((p) => p.code === currentCode)
  const currentCredits = current?.monthly_credits ?? 0
  return plans
    .filter(
      (p) =>
        !p.is_managed &&
        p.code !== 'free' &&
        (p.stripe_price_id_monthly || p.stripe_price_id_yearly) &&
        p.code !== currentCode &&
        p.monthly_credits > currentCredits
    )
    .sort((a, b) => a.monthly_credits - b.monthly_credits)
}

/**
 * The smallest tier that solves the customer's problem: for a missing feature
 * the first tier that includes it, otherwise the next tier up. Null when
 * nothing above them is for sale.
 */
export function recommendUpgrade<T extends UpgradeCandidate>(
  candidates: T[],
  reason: UpgradeReason
): T | null {
  if (reason.kind === 'feature') {
    return (
      candidates.find((p) => hasFeature(p.features, reason.feature)) ?? null
    )
  }
  return candidates[0] ?? null
}

type Listener = (reason: UpgradeReason) => void

const listeners = new Set<Listener>()

/**
 * Lets code outside React — a toast's action, a download helper — ask the
 * mounted upgrade dialog to open. One provider subscribes; nothing happens
 * when none is mounted.
 */
let available = false
/** The org in view's spendable credits, or null when unknown; the provider keeps it current. */
let readCredits: () => number | null = () => null

export const upgradeEvents = {
  open(reason: UpgradeReason) {
    for (const listener of listeners) {
      listener(reason)
    }
  },
  /** Whether opening would do anything for the current user; the provider keeps it current. */
  get available(): boolean {
    return available
  },
  setAvailable(next: boolean): () => void {
    available = next
    return () => {
      available = false
    }
  },
  /**
   * True when the org in view has nothing left to spend, so an action that
   * costs credits can be refused at once instead of after a server round
   * trip. Unknown (no org, still loading) is never "out": the server decides.
   */
  outOfCredits(): boolean {
    const remaining = readCredits()
    return remaining !== null && remaining <= 0
  },
  setCreditsReader(reader: () => number | null): () => void {
    readCredits = reader
    return () => {
      readCredits = () => null
    }
  },
  subscribe(listener: Listener): () => void {
    listeners.add(listener)
    return () => {
      listeners.delete(listener)
    }
  },
}

/** What a new self-serve account starts with; mirrors `create_self_serve_account`. */
export const WELCOME_CREDITS = 50

export type PlanChangeKind =
  'same' | 'checkout' | 'upgrade' | 'downgrade' | 'cancel'

/**
 * What choosing `target` at `targetInterval` means for someone on `current`.
 * Upgrades take effect now and are charged now; downgrades — a smaller tier,
 * or a shorter interval on the same tier — wait for the period end so the
 * customer keeps what they paid for; Free is a cancellation at period end.
 */
export function planChangeKind(input: {
  current: { code: string; monthly_credits: number } | null
  currentInterval: 'month' | 'year' | null
  subscribed: boolean
  target: { code: string; monthly_credits: number }
  targetInterval: 'month' | 'year'
}): PlanChangeKind {
  const { current, currentInterval, subscribed, target, targetInterval } = input
  if (!subscribed) {
    return target.code === 'free' ? 'same' : 'checkout'
  }
  if (target.code === 'free') {
    return 'cancel'
  }
  if (current?.code === target.code && currentInterval === targetInterval) {
    return 'same'
  }
  const shorter = currentInterval === 'year' && targetInterval === 'month'
  const smaller = target.monthly_credits < (current?.monthly_credits ?? 0)
  return smaller || shorter ? 'downgrade' : 'upgrade'
}

/** Every self-serve tier a customer can be on, Free first, largest last. */
export function planLadder<T extends UpgradeCandidate>(plans: T[]): T[] {
  return plans
    .filter(
      (p) =>
        !p.is_managed &&
        (p.code === 'free' ||
          p.stripe_price_id_monthly ||
          p.stripe_price_id_yearly)
    )
    .sort((a, b) => a.monthly_credits - b.monthly_credits)
}
